import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "container", "template", "typeSelect", "labelInput",
    "optionsSection", "chipsContainer", "optionInput", "optionsError",
    "addArea", "limitMessage"
  ]
  static values = {
    max: { type: Number, default: 99 },
    maxSimple: { type: Number, default: 5 },
    overLimitTpl: { type: String, default: "" }
  }

  connect() {
    this.visibleRows.forEach(row => row.setAttribute("draggable", "true"))
    this.element.addEventListener("dragstart", this._onDragStart.bind(this))
    this.element.addEventListener("dragover", this._onDragOver.bind(this))
    this.element.addEventListener("drop", this._onDrop.bind(this))
    this.element.addEventListener("dragend", this._onDragEnd.bind(this))
    this._onTypeChanged = ({ detail: { type } }) => this._applyTypeLimit(type)
    document.addEventListener("campaign-type:changed", this._onTypeChanged)
    this._updateAddArea()
  }

  disconnect() {
    document.removeEventListener("campaign-type:changed", this._onTypeChanged)
  }

  typeChanged() {
    const isSelect = this.typeSelectTarget.value === "select"
    this.optionsSectionTarget.style.display = isSelect ? "block" : "none"
    if (!isSelect) this._clearOptions()
    if (this.hasOptionsErrorTarget) this.optionsErrorTarget.style.display = "none"
  }

  addOption() {
    const val = this.optionInputTarget.value.trim()
    if (!val) {
      this._flashBorder(this.optionInputTarget)
      this.optionInputTarget.focus()
      return
    }
    if (this._currentOptions().includes(val)) {
      this._flashBorder(this.optionInputTarget, "#ef4444")
      this.optionInputTarget.focus()
      return
    }
    this._appendChip(val)
    this.optionInputTarget.value = ""
    this.optionInputTarget.focus()
    if (this.hasOptionsErrorTarget) this.optionsErrorTarget.style.display = "none"
  }

  removeOption(event) {
    event.currentTarget.closest("[data-option-chip]").remove()
  }

  add() {
    const count = this.visibleRows.length
    if (count >= this.maxValue) return

    const type = this.typeSelectTarget.value
    const label = this.labelInputTarget.value.trim()
    if (!label) {
      this._flashBorder(this.labelInputTarget)
      this.labelInputTarget.focus()
      return
    }

    let options = []
    if (type === "select") {
      options = this._currentOptions()
      if (options.length < 2) {
        if (this.hasOptionsErrorTarget) this.optionsErrorTarget.style.display = "block"
        this.optionInputTarget?.focus()
        return
      }
    }

    const index = Date.now()
    const optionsHtml = options.map(o =>
      `<input type="hidden" name="test_campaign[campaign_report_fields_attributes][${index}][options][]" value="${this._escapeHtml(o)}">`
    ).join("")

    const html = this.templateTarget.innerHTML
      .replace(/__INDEX__/g, index)
      .replace(/__TYPE__/g, this._escapeHtml(type))
      .replace(/__LABEL__/g, this._escapeHtml(label))
      .replace(/<!--\s*__OPTIONS_HTML__\s*-->/g, optionsHtml)

    const wrapper = document.createElement("div")
    wrapper.innerHTML = html.trim()
    const newRow = wrapper.firstElementChild
    newRow.setAttribute("draggable", "true")
    this.containerTarget.appendChild(newRow)

    this.labelInputTarget.value = ""
    if (type === "select") this._clearOptions()
    this._updatePositions()
    this._updateAddArea()
    this._notifyCampaignFormChanged()
  }

  remove(event) {
    const row = event.currentTarget.closest("[data-report-field-row]")
    if (!row) return

    const destroyInput = row.querySelector("[data-destroy-field]")
    if (destroyInput) {
      destroyInput.value = "1"
      row.style.display = "none"
    } else {
      row.remove()
    }
    this._updatePositions()
    this._updateAddArea()
    this._notifyCampaignFormChanged()
  }

  toggleRequired(event) {
    const row = event.currentTarget.closest("[data-report-field-row]")
    if (!row) return

    const input = row.querySelector("[data-required-input]")
    const badge = event.currentTarget
    const nowRequired = input.value !== "true"

    input.value = nowRequired ? "true" : "false"

    if (nowRequired) {
      badge.textContent = badge.dataset.labelRequired || "required"
      badge.style.color = "var(--ls-accent)"
      badge.style.fontWeight = "600"
    } else {
      badge.textContent = badge.dataset.labelOptional || "optional"
      badge.style.color = "rgba(15,23,42,0.35)"
      badge.style.fontWeight = "500"
    }
  }

  _applyTypeLimit(type) {
    this.maxValue = type === "simple" ? this.maxSimpleValue : 99
    this._updateAddArea()
  }

  _updateAddArea() {
    const count = this.visibleRows.length
    const max = this.maxValue
    const atMax = count >= max
    const overMax = count > max

    if (this.hasAddAreaTarget) {
      this.addAreaTarget.style.display = atMax ? "none" : ""
    }

    if (this.hasLimitMessageTarget) {
      const el = this.limitMessageTarget
      if (overMax) {
        el.style.display = "block"
        el.style.cssText += "; background: rgba(239,68,68,0.07); border-color: rgba(239,68,68,0.25); color: #dc2626;"
        el.textContent = this._renderTpl(this.overLimitTplValue, count, max, count - max)
        el.dataset.overLimit = "true"
      } else if (atMax) {
        el.style.display = "block"
        el.style.cssText += "; background: rgba(217,119,6,0.07); border-color: rgba(217,119,6,0.2); color: #d97706;"
        el.textContent = el.dataset.atLimitText || el.textContent
        delete el.dataset.overLimit
      } else {
        el.style.display = "none"
        delete el.dataset.overLimit
      }
    }
  }

  _onDragStart(event) {
    const row = event.target.closest("[data-report-field-row]")
    if (!row) return
    this.draggedRow = row
    row.style.opacity = "0.4"
    event.dataTransfer.effectAllowed = "move"
    event.dataTransfer.setData("text/plain", "")
  }

  _onDragOver(event) {
    event.preventDefault()
    const target = event.target.closest("[data-report-field-row]")
    if (!target || target === this.draggedRow) return
    event.dataTransfer.dropEffect = "move"

    const bounding = target.getBoundingClientRect()
    const midY = bounding.y + bounding.height / 2
    if (event.clientY < midY) {
      this.containerTarget.insertBefore(this.draggedRow, target)
    } else {
      this.containerTarget.insertBefore(this.draggedRow, target.nextSibling)
    }
  }

  _onDrop(event) {
    event.preventDefault()
    this._updatePositions()
  }

  _onDragEnd(event) {
    const row = event.target.closest("[data-report-field-row]")
    if (row) row.style.opacity = ""
    this.draggedRow = null
    this._updatePositions()
  }

  get visibleRows() {
    return Array.from(
      this.containerTarget.querySelectorAll("[data-report-field-row]:not([style*='display: none'])")
    )
  }

  _updatePositions() {
    this.visibleRows.forEach((row, i) => {
      const pos = row.querySelector("[data-position-field]")
      if (pos) pos.value = i
    })
    this._notifyCampaignFormChanged()
  }

  _notifyCampaignFormChanged() {
    this.element.closest("form")?.dispatchEvent(new CustomEvent("campaign-form:changed", { bubbles: true }))
  }

  _currentOptions() {
    return Array.from(
      this.chipsContainerTarget.querySelectorAll("[data-option-chip]")
    ).map(el => el.dataset.optionValue)
  }

  _appendChip(val) {
    const chip = document.createElement("span")
    chip.setAttribute("data-option-chip", "true")
    chip.setAttribute("data-option-value", val)
    chip.style.cssText = "display: inline-flex; align-items: center; gap: 4px; background: rgba(var(--ls-accent-rgb),0.08); border: 1px solid rgba(var(--ls-accent-rgb),0.2); border-radius: 6px; padding: 3px 8px; font-size: 12px; color: var(--ls-accent); font-weight: 500;"
    chip.innerHTML = `<span>${this._escapeHtml(val)}</span><button type="button" data-action="click->report-fields#removeOption" aria-label="Remove" style="display: inline-flex; align-items: center; background: none; border: none; cursor: pointer; padding: 0 0 0 2px; color: var(--ls-accent); opacity: 0.55; font-size: 15px; line-height: 1; transition: opacity 0.15s;" onmouseover="this.style.opacity='1'" onmouseout="this.style.opacity='0.55'">×</button>`
    this.chipsContainerTarget.appendChild(chip)
  }

  _clearOptions() {
    if (this.hasChipsContainerTarget) this.chipsContainerTarget.innerHTML = ""
  }

  _flashBorder(el, color = "var(--ls-accent)") {
    el.style.borderColor = color
    setTimeout(() => { el.style.borderColor = "" }, 1500)
  }

  _renderTpl(tpl, count, max, excess) {
    if (!tpl) return `${count}/${max} — remove ${excess}`
    return tpl
      .replace("{{count}}", count)
      .replace("{{max}}", max)
      .replace("{{excess}}", excess)
  }

  _escapeHtml(str) {
    return str
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
  }
}
